import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable24() {
  const pdfRef = useRef();

  const data = [
    { year: 2018, "Asian Alone": 64, "Black or African American Alone": 75 },
    { year: 2019, "Asian Alone": 69, "Black or African American Alone": 78 },
    { year: 2020, "Asian Alone": 87, "Black or African American Alone": 104 },
    { year: 2021, "Asian Alone": 92, "Black or African American Alone": 106 },
    { year: 2022, "Asian Alone": 107, "Black or African American Alone": 131 },
    { year: 2023, "Asian Alone": 118, "Black or African American Alone": 165 },
    { year: 2024, "Asian Alone": 135, "Black or African American Alone": 188 },
  ];

  const years = data.map((d) => d.year);

  // Chart Data
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Asian Alone",
        data: data.map((d) => d["Asian Alone"]),
        borderColor: "#10B981",
        backgroundColor: "#10B981",
        tension: 0.35,
        borderWidth: 3,
        pointRadius: 4,
      },
      {
        label: "Black or African American Alone",
        data: data.map((d) => d["Black or African American Alone"]),
        borderColor: "#374151",
        backgroundColor: "#374151",
        tension: 0.35,
        borderWidth: 3,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: true,
        text: "📊 Employment Trend: Asian vs Black or African American (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" }, grid: { display: false } },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
      },
    },
  };

  // Summary Insights
  const asian2018 = data[0]["Asian Alone"];
  const asian2024 = data[data.length - 1]["Asian Alone"];
  const black2018 = data[0]["Black or African American Alone"];
  const black2024 = data[data.length - 1]["Black or African American Alone"];

  const asianGrowth = (((asian2024 - asian2018) / asian2018) * 100).toFixed(1);
  const blackGrowth = (((black2024 - black2018) / black2018) * 100).toFixed(1);

  const fasterGroup =
    parseFloat(asianGrowth) > parseFloat(blackGrowth)
      ? "Asian Alone"
      : "Black or African American Alone";

  const gap2024 = Math.abs(black2024 - asian2024);

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_trend_asian_vs_black.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Trend: Asian vs Black or African American (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Asian Alone Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">+{asianGrowth}%</p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Black or African American Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-pink-800">+{blackGrowth}%</p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              2024 Employment Gap
            </p>
            <p className="text-xl font-bold text-green-800">
              {gap2024.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">Higher in {fasterGroup}</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
            <div className="overflow-x-auto flex items-center">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Asian Alone</th>
                    <th className="p-2">Black or African American Alone</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.year} className="hover:bg-gray-100">
                      <td className="p-2">{row.year}</td>
                      <td className="p-2">
                        {row["Asian Alone"].toLocaleString()}
                      </td>
                      <td className="p-2">
                        {row[
                          "Black or African American Alone"
                        ].toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          {/* </div> */}

          {/* Chart */}
          <div className="flex flex-col h-96">
            <div className="bg-white rounded-lg shadow-md p-4 flex-grow">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
